import { bulkCreateChecks } from 'admin/actions/checks';
import Encoding from 'encoding-japanese';
import moment from 'moment';
import React, { useEffect } from 'react';
import { CSVLink } from 'react-csv';
import { useDropzone } from 'react-dropzone';
import { readString } from 'react-papaparse';
import { Alert } from 'reactstrap';
import styled from 'styled-components';
import { Button } from '../../atoms/Buttons';
import FA from '../../atoms/FontAwesome';
import Loader from '../../atoms/Loader';
import BreadCrumb from '../../molecules/BreadCrumb';

const CRUMBS = [
  { value: 'ホーム', href: '/', isCurrentPage: false },
  { value: '販促管理', href: '', isCurrentPage: true },
  { value: 'チャージQRコード管理', href: '/promotion/checks', isCurrentPage: false },
  { value: 'チャージQRコード一括登録', href: '', isCurrentPage: true },
];

const MAX_LINES = 1000;

const DATE_FIELDS = ['開始日時', '終了日時', 'ポイント有効期限（日付指定）'];

// サーバの +serial-code-chars+ (models/check.lisp) と同じ文字集合。誤読防止のため O・0・
// 小文字が除かれているので、ここを [A-Za-z0-9] にするとアップロード後にサーバだけが弾く
const CODE_PREFIX_PATTERN = /^[A-NP-Z1-9]{4}$/;

const CHECK_GUIDE_LINK =
  'https://pocket-change.notion.site/QR-18c068200e1080e09fa4ca3eaf7a421a#23e068200e108021b9bddd67f16484de';

const Dropzone = styled.div<{ fileSelected?: boolean }>`
  border: 2px dashed #d6d3d0;
  background-color: #f8f7f6;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: ${({ fileSelected = false }) => (fileSelected ? 'flex-start' : 'center')};
  gap: 16px;
  padding: 8px;
  min-height: 300px;
  cursor: default;
  position: relative;
`;

const DropzoneText = styled.p`
  font-size: 20px;
  color: #706d65;
  text-align: center;
  margin: 0;
`;

const FileSelectButton = styled(Button)`
  color: black;
  font-size: 20px;
  display: flex;
  gap: 12px;
  padding: 12px 20px;
  border-radius: 8px;
`;

const FileClearButton = styled(FA)`
  padding: 4px;
  cursor: pointer;
`;

const UploadCancelButton = styled(FA)`
  position: absolute;
  padding: 4px;
  top: 4px;
  right: 8px;
  cursor: pointer;
`;

const NotesWrapper = styled.div`
  margin: 16px 0;

  .font-awesome-icon {
    color: #ff8800;
  }

  ul {
    list-style-type: none;
    padding: 0;
    margin: 0;

    li {
      margin-bottom: 4px;
    }
  }

  a {
    text-decoration: underline;
  }
`;

export const validateCSVLine = (row: any, idx: number) => {
  const EMPTY_CHECK_FIELDS = [
    'マネーフルID',
    'チャージ元店舗フルID',
    '用途',
    'チャージポイント',
    'チャージマネー',
    '終了日時',
  ];
  const errors: { line: number; message: string }[] = [];
  EMPTY_CHECK_FIELDS.forEach((field) => {
    if (!row[field]) {
      errors.push({ line: idx, message: `${field}を入力してください` });
    }
  });

  const LENGTH_CHECK_FIELDS = [
    {
      name: '用途',
      length: 32,
    },
    {
      name: '説明文',
      length: 64,
    },
  ];

  LENGTH_CHECK_FIELDS.forEach((field) => {
    if ((row[field.name]?.length ?? 0) > field.length) {
      errors.push({
        line: idx,
        message: `${field.name}は${field.length}文字以内で入力してください`,
      });
    }
  });

  const NUMBER_CHECK_FIELDS = [
    'チャージポイント',
    'チャージマネー',
    'ポイント有効期限（相対日数指定）',
  ];

  NUMBER_CHECK_FIELDS.forEach((field) => {
    if (isNaN(row[field]) || Number(row[field]) < 0) {
      errors.push({ line: idx, message: `${field}は数字を入力してください` });
    }
  });

  const usageLimit = row['受け取り可能人数'];
  if (usageLimit && (isNaN(usageLimit) || Number(usageLimit) < 1)) {
    errors.push({ line: idx, message: `受け取り可能人数は1以上の数字を入力してください` });
  }

  const codePrefix = row['コードプレフィックス'];
  if (codePrefix && !CODE_PREFIX_PATTERN.test(codePrefix)) {
    errors.push({ line: idx, message: 'コードプレフィックスは半角英数4文字で入力してください' });
  }

  const FUTURE_ONLY_DATE_FIELDS = ['終了日時', 'ポイント有効期限（日付指定）'];
  DATE_FIELDS.forEach((field) => {
    if (!row[field]) {
      return;
    }
    if (!moment(row[field], 'YYYY/MM/DD HH:mm:ss', true).isValid()) {
      errors.push({ line: idx, message: `${field}が不正です` });
    } else if (FUTURE_ONLY_DATE_FIELDS.includes(field) && moment(row[field]).isBefore(moment())) {
      errors.push({ line: idx, message: `${field}は本日以降を入力してください` });
    }
  });

  return errors;
};

const validateCSVData = async (data: any[]) => {
  const errors: { line: number; message: string }[] = [];
  data.forEach((row, idx) => {
    const rowErrors = validateCSVLine(row, idx + 2);
    errors.push(...rowErrors);
  });
  return errors;
};

const BulkCreateChecks = () => {
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null);
  const [fileError, setFileError] = React.useState<
    'zero-byte' | 'over-limit' | 'wrong-encoding' | 'none' | 'csv-errors'
  >('none');
  const [formState, setFormState] = React.useState<'initial' | 'loading' | 'done'>('initial');
  const [abortController, setAbortController] = React.useState<AbortController | null>(null);
  const [csvErrors, setCsvErrors] = React.useState<{ line: number; message: string }[]>([]);
  const [successCount, setSuccessCount] = React.useState<number>(-1);

  const { getRootProps, getInputProps, open } = useDropzone({
    maxFiles: 1,
    accept: '.csv',
    noClick: true,
    onDrop: (acceptedFiles) => setSelectedFile(acceptedFiles[0]),
  });

  useEffect(() => {
    if (!selectedFile) {
      setFileError('none');
      return;
    }

    if (selectedFile.size === 0) {
      setFileError('zero-byte');
      return;
    }

    readContentString(selectedFile).then(async (csvString) => {
      const encoding = Encoding.detect(csvString);

      if (csvString.trim().length === 0) {
        setFileError('zero-byte');
        return;
      }

      if (!(encoding === 'UTF8' || encoding === 'SJIS' || encoding === 'UNICODE')) {
        setFileError('wrong-encoding');
        return;
      }

      const utf8CsvString = encoding === 'UTF8' ? Encoding.convert(csvString, 'UTF8') : csvString;

      const data = await getCSVData(utf8CsvString);

      if (data.length > MAX_LINES) {
        setFileError('over-limit');
        return;
      }

      const csvErrors = await validateCSVData(data);
      if (csvErrors.length > 0) {
        setCsvErrors(csvErrors);
        setFileError('csv-errors');
        return;
      }

      setFileError('none');
    });
  }, [selectedFile]);

  const handleSubmit = async () => {
    if (!selectedFile || fileError !== 'none') {
      return;
    }

    setCsvErrors([]);

    const controller = new AbortController();
    setAbortController(controller);

    setFormState('loading');

    const csvString = await readContentString(selectedFile);
    const data: any[] = await getCSVData(csvString, false);

    const header: string[] = data[0] ?? [];
    const dateIndices = DATE_FIELDS.map((field) => header.indexOf(field)).filter((i) => i >= 0);

    const transfromed = data.map((row, idx) => {
      if (idx === 0) {
        return row;
      }

      dateIndices.forEach((i) => {
        row[i] = row[i] ? moment(row[i]).toISOString() : '';
      });

      return row;
    });

    try {
      const res = await bulkCreateChecks({
        name: Date.now().toString(),
        descripton: `${Date.now().toString()}_bulk_create`,
        csv: new File([transfromed.map((row) => row.join(',')).join('\n')], selectedFile.name, {
          type: 'text/csv',
        }),
        signal: controller.signal,
      });

      setSuccessCount((res as unknown[]).length);
    } catch (e) {
      const error = await Promise.resolve(e);
      if (error.name === 'AbortError') {
        setFormState('initial');
        return;
      }
      if (error?.error === 'no data inserted') {
        setSuccessCount(0);
      } else if (error?.errors && error.errors.length > 0) {
        if (error?.type === 'invalid_bulk_checks_csv') {
          setCsvErrors(error.errors.map((err: any) => ({ line: err.line, message: err.message })));
          setFileError('csv-errors');
        } else {
          const errors = error.errors.reverse();
          const csvErrors: { line: number; message: string }[] = [];
          let errorIndex = 0;
          const csvData: any[] = await getCSVData(csvString);
          csvData.forEach((row, idx) => {
            const rowError = errors[errorIndex];
            if (
              row['マネーフルID'] === rowError.money_id &&
              row['チャージ元店舗フルID'] === rowError.shop_id &&
              row['ポイント負担先店舗フルID'] === rowError.bear_point_account_id
            ) {
              csvErrors.push({ line: idx + 2, message: rowError.error_triggered.MESSAGE });
              errorIndex++;
            }
          });
          setCsvErrors(csvErrors);
          setFileError('csv-errors');
        }
      }
    }
    setFormState('done');
  };

  return (
    <>
      <BreadCrumb crumbs={CRUMBS} />
      <h5>チャージQRコード一括登録</h5>
      <Alert isOpen={fileError !== 'none'} color="danger">
        {fileError === 'zero-byte' && 'ファイル容量が0byteになっています'}
        {fileError === 'over-limit' && 'データ件数は1,000件以下にしてください'}
        {fileError === 'wrong-encoding' &&
          'ファイルの文字コード�UTF-8、Shift-JIS、Unicodeのいずれかである必要があります'}
        {fileError === 'csv-errors' && (
          <>
            <p>{[...new Set(csvErrors.map((x) => x.line))].length}件の登録に失敗しました</p>
            <p>
              <CSVLink
                data={csvErrors.map(({ line, message }) => [`${line}行目：${message}`])}
                filename={`Error_${moment().format('yyyyMMDDhhmmss')}.csv`}
                style={{ color: 'red', textDecoration: 'underline' }}
              >
                エラーファイル
              </CSVLink>
              からエラー箇所を確認してください
            </p>
          </>
        )}
      </Alert>
      <Alert isOpen={successCount !== -1} color="success">
        {successCount}件の登録に成功しました
      </Alert>
      {formState !== 'done' && (
        <Dropzone {...getRootProps()} fileSelected={!!selectedFile && formState !== 'loading'}>
          {formState === 'initial' && (
            <>
              <input {...getInputProps()} />
              {selectedFile ? (
                <>
                  <span>
                    <b>選択中のファイル</b>
                  </span>
                  <span>
                    <b>
                      {selectedFile.name}{' '}
                      <FileClearButton icon="times-circle" onClick={() => setSelectedFile(null)} />
                    </b>
                  </span>
                </>
              ) : (
                <>
                  <DropzoneText>ここにドラッグ&ドロップ</DropzoneText>
                  <DropzoneText>または</DropzoneText>
                  <FileSelectButton onClick={open} variant="muted-outline">
                    <FA icon="plus-circle" /> ファイルを選択
                  </FileSelectButton>
                </>
              )}
            </>
          )}

          {formState === 'loading' && (
            <>
              <Loader type="Oval" width={50} height={50} />
              <span>処理中です。しばらくお待ちください</span>
              <UploadCancelButton
                icon="times"
                onClick={() => {
                  abortController?.abort();
                  setFormState('initial');
                }}
              />
            </>
          )}
        </Dropzone>
      )}
      {formState !== 'done' && (
        <>
          <NotesWrapper>
            <h6>
              <FA icon="exclamation-triangle" /> CSVインポートに関する注意事項
            </h6>
            <ul>
              <li>
                CSVファイルの作成方法は
                <a href={CHECK_GUIDE_LINK} target="_blank" rel="noopener noreferrer">
                  こちら
                </a>
              </li>
              <li>
                ダウンロードファイルから取得したヘッダーは削除しないで下さい。（1行目はヘッダー行として扱います）
              </li>
              <li>インポート対象は、チャージQRコードIDの指定が無い行を対象に行います。</li>
              <li>インポートができない行があった場合、インポートできる行のみ取り込みます。</li>
            </ul>
          </NotesWrapper>
          <Button onClick={handleSubmit} disabled={formState === 'loading'}>
            登録
          </Button>
        </>
      )}
    </>
  );
};

function readContentString(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      if (!event.target || typeof event.target.result !== 'string') {
        reject();
        return;
      }
      const { result } = event.target;
      resolve(result);
    };
    reader.onerror = () => reject();

    reader.readAsText(file);
  });
}

async function getCSVData(csvString: string, header = true) {
  return new Promise<unknown[]>((resolve) => {
    readString(csvString, {
      worker: true,
      header,
      skipEmptyLines: 'greedy',
      complete: (results) => {
        resolve(results.data);
      },
    });
  });
}

export default BulkCreateChecks;
